<script setup lang="ts">
import { computed } from 'vue'
import { threadsUrls } from '@/util/links'

type ThreadKey = 'annual-iep' | 'bsp' | 'transition-plan'

type ThreadLink = {
  key: ThreadKey
  label: string
  href: string
}

const props = withDefaults(defineProps<{
  isCcsd?: boolean
  isTransitionVisible?: boolean
  isBspVisible?: boolean
}>(), {
  isCcsd: false,
  isTransitionVisible: true,
  isBspVisible: true
})

const ALL_THREADS: ThreadLink[] = [
  { key: 'annual-iep', label: 'Annual IEP', href: threadsUrls.annualIEP },
  { key: 'bsp', label: 'Behavior Support Plan', href: threadsUrls.bsp },
  { key: 'transition-plan', label: 'Transition Plan', href: threadsUrls.transitionPlan }
]

const CCSD_SUPPRESSED_THREADS: ThreadKey[] = ['bsp']

function isOffered(thread: ThreadLink): boolean {
  const isSuppressedForCcsd = props.isCcsd && CCSD_SUPPRESSED_THREADS.includes(thread.key)
  const isExcludedTransitionPlan = thread.key === 'transition-plan' && !props.isTransitionVisible
  const isExcludedBsp = thread.key === 'bsp' && !props.isBspVisible
  return !isSuppressedForCcsd && !isExcludedTransitionPlan && !isExcludedBsp
}

function withDistrictHref(thread: ThreadLink): ThreadLink {
  const isCcsdAnnualIEP = thread.key === 'annual-iep' && props.isCcsd
  if (!isCcsdAnnualIEP) {
    return thread
  }
  return { ...thread, href: threadsUrls.annualIEPCCSD }
}

const threads = computed(() => ALL_THREADS.filter(isOffered).map(withDistrictHref))
</script>

<template>
  <div>
    <!-- Same 7px top bar as InstructionalDesignCard, in the gold from the Threads logo -->
    <div class="h-[7px] w-full bg-[#C9AA2D]" />
    <section class="border-2 border-t-0 border-[#BDBDBD] p-5" aria-label="Goalbook Threads">
      <div class="flex flex-col items-center gap-[14px]">
        <img alt="Threads logo" src="@/assets/threads_logo.svg" class="h-[30px] w-auto">
        <p class="m-0 text-gray-900">Draft research-based, individualized plans from start to finish using AI.</p>
        <ul class="m-0 flex list-none flex-wrap justify-center gap-4 p-0">
          <li v-for="thread in threads" :key="thread.key" class="m-0 pt-2" data-testid="thread-link">
            <a
              :href="thread.href"
              class="flex min-w-[220px] items-center justify-center rounded-[8px] border border-[#23537B] px-6 py-4 text-center text-lg font-[600] text-[#23537B] no-underline hover:bg-[#EEEEEE] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#23537B]"
            >
              {{ thread.label }}
            </a>
          </li>
        </ul>
      </div>
    </section>
  </div>
</template>
